<template>
  <aside class="filter-panel" aria-label="Experiment filters">
    <div class="panel-header">
      <h4>Filters</h4>
      <button type="button" class="link-button" :disabled="loading" @click="$emit('clear')">
        Clear all
      </button>
    </div>

    <form @submit.prevent="$emit('apply')">
      <details class="filter-section">
        <summary>
          Project
          <span v-if="modelValue.projects.length" class="badge">{{ modelValue.projects.length }}</span>
        </summary>
        <multiselect
          id="filter-projects"
          :model-value="modelValue.projects"
          :options="projects"
          :multiple="true"
          :close-on-select="false"
          label="name"
          track-by="id"
          placeholder="Any project"
          aria-label="Project"
          @update:model-value="update('projects', $event)"
        />
      </details>

      <details class="filter-section">
        <summary>
          Location
          <span v-if="modelValue.locations.length" class="badge">{{ modelValue.locations.length }}</span>
        </summary>
        <multiselect
          id="filter-locations"
          :model-value="modelValue.locations"
          :options="options.locations || []"
          :multiple="true"
          :close-on-select="false"
          label="name"
          track-by="id"
          placeholder="Any location"
          aria-label="Location"
          @update:model-value="update('locations', $event)"
        />
      </details>

      <details class="filter-section">
        <summary>
          Instrument
          <span v-if="modelValue.instruments.length" class="badge">{{ modelValue.instruments.length }}</span>
        </summary>
        <multiselect
          id="filter-instruments"
          :model-value="modelValue.instruments"
          :options="options.instruments || []"
          :multiple="true"
          :close-on-select="false"
          :custom-label="serialLabel"
          track-by="id"
          placeholder="Any instrument"
          aria-label="Instrument"
          @update:model-value="update('instruments', $event)"
        />
      </details>

      <details class="filter-section">
        <summary>
          Material
          <span v-if="modelValue.materials.length" class="badge">{{ modelValue.materials.length }}</span>
        </summary>
        <multiselect
          id="filter-materials"
          :model-value="modelValue.materials"
          :options="options.materials || []"
          :multiple="true"
          :close-on-select="false"
          :custom-label="batchLabel"
          track-by="id"
          placeholder="Any material"
          aria-label="Material"
          @update:model-value="onMaterialsChange"
        />
        <template v-if="modelValue.materials.length">
          <label class="field-label" for="filter-relation-types">Relation to experiment</label>
          <multiselect
            id="filter-relation-types"
            :model-value="modelValue.relationTypes"
            :options="options.material_relation_types || []"
            :multiple="true"
            :close-on-select="false"
            label="name"
            track-by="id"
            placeholder="Any relation"
            @update:model-value="update('relationTypes', $event)"
          />
        </template>
      </details>

      <details class="filter-section">
        <summary>
          Date range
          <span v-if="dateCount" class="badge">{{ dateCount }}</span>
        </summary>
        <div class="date-row">
          <div>
            <label class="field-label" for="filter-start-date">From</label>
            <input
              id="filter-start-date"
              type="date"
              :value="modelValue.startDate"
              :max="modelValue.endDate || null"
              @input="update('startDate', $event.target.value)"
            />
          </div>
          <div>
            <label class="field-label" for="filter-end-date">To</label>
            <input
              id="filter-end-date"
              type="date"
              :value="modelValue.endDate"
              :min="modelValue.startDate || null"
              @input="update('endDate', $event.target.value)"
            />
          </div>
        </div>
      </details>

      <details class="filter-section">
        <summary>
          Metadata
          <span v-if="modelValue.metadata.length" class="badge">{{ modelValue.metadata.length }}</span>
        </summary>
        <p v-if="!metadataLists.length" class="hint">No metadata available to filter on.</p>

        <template v-else>
          <label class="field-label" for="filter-metadata-lists">Metadata lists</label>
          <multiselect
            id="filter-metadata-lists"
            :model-value="modelValue.metadataLists"
            :options="metadataLists"
            :multiple="true"
            :close-on-select="false"
            label="name"
            track-by="id"
            placeholder="Select relevant lists"
            @update:model-value="onListsChange"
          />
          <p v-if="!modelValue.metadataLists.length" class="hint">
            Select one or more lists to filter on their keys.
          </p>
        </template>

        <ul class="md-rows">
          <li v-for="(row, index) in modelValue.metadata" :key="row.uid" class="md-row">
            <multiselect
              :model-value="row.key"
              :options="metadataKeyGroups"
              group-values="keys"
              group-label="listName"
              :group-select="false"
              :custom-label="keyLabel"
              track-by="id"
              :allow-empty="false"
              placeholder="Select key"
              aria-label="Metadata key"
              @update:model-value="onKeyChange(index, $event)"
            />

            <template v-if="row.key">
              <select
                :value="row.operator"
                aria-label="Operator"
                @change="updateRow(index, { operator: $event.target.value, value2: '' })"
              >
                <option v-for="op in row.key.operators" :key="op" :value="op">
                  {{ operatorLabel(op) }}
                </option>
              </select>

              <div class="value-inputs">
                <template v-for="field in valueFields(row)" :key="field">
                  <select
                    v-if="row.key.value_type === 'boolean'"
                    :value="row[field]"
                    :aria-label="valueAria(row, field)"
                    @change="updateRow(index, { [field]: $event.target.value })"
                  >
                    <option value="">Select…</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                  <input
                    v-else
                    :type="inputType(row.key.value_type)"
                    :step="row.key.value_type === 'float' ? 'any' : null"
                    :value="row[field]"
                    :aria-label="valueAria(row, field)"
                    :aria-invalid="rowError(row) ? 'true' : null"
                    @input="updateRow(index, { [field]: $event.target.value })"
                  />
                </template>
                <span v-if="row.key.unit" class="unit">{{ row.key.unit }}</span>
              </div>
            </template>

            <p v-if="rowError(row)" class="field-error" role="alert">{{ rowError(row) }}</p>

            <button type="button" class="link-button remove" @click="removeRow(index)">
              Remove
            </button>
          </li>
        </ul>

        <button
          v-if="metadataKeyGroups.length"
          type="button"
          class="link-button"
          @click="addRow"
        >
          + Add metadata filter
        </button>
      </details>

      <div class="panel-footer">
        <button type="submit" class="apply-button" :disabled="loading || hasErrors">
          Apply filters
        </button>
      </div>
    </form>
  </aside>
</template>

<script>
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.css';
import {
  NO_VALUE_OPERATORS,
  OPERATOR_LABELS,
  newMetadataRow,
  validateMetadataRow,
} from '@/utils/experimentFilters.js';

export default {
  name: 'ExperimentFilterPanel',
  components: { Multiselect },
  props: {
    modelValue: { type: Object, required: true },
    options: { type: Object, default: () => ({}) },
    projects: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
  },
  emits: ['update:modelValue', 'apply', 'clear'],
  computed: {
    metadataLists() {
      return (this.options.metadata_lists || []).filter((list) => list.keys?.length);
    },
    metadataKeyGroups() {
      const selected = new Set(this.modelValue.metadataLists.map((list) => list.id));
      return this.metadataLists
        .filter((list) => selected.has(list.id))
        .map((list) => ({
          listName: list.name,
          keys: list.keys.map((key) => ({ ...key, listId: list.id, listName: list.name })),
        }));
    },
    dateCount() {
      return Number(Boolean(this.modelValue.startDate)) + Number(Boolean(this.modelValue.endDate));
    },
    hasErrors() {
      return this.modelValue.metadata.some((row) => validateMetadataRow(row).status === 'invalid');
    },
  },
  methods: {
    update(field, value) {
      this.$emit('update:modelValue', { ...this.modelValue, [field]: value });
    },
    onMaterialsChange(materials) {
      this.$emit('update:modelValue', {
        ...this.modelValue,
        materials,
        relationTypes: materials.length ? this.modelValue.relationTypes : [],
      });
    },
    onListsChange(metadataLists) {
      const selected = new Set(metadataLists.map((list) => list.id));
      this.$emit('update:modelValue', {
        ...this.modelValue,
        metadataLists,
        metadata: this.modelValue.metadata.filter((row) => !row.key || selected.has(row.key.listId)),
      });
    },
    serialLabel(item) {
      return item.serial ? `${item.name} (${item.serial})` : item.name;
    },
    batchLabel(item) {
      return item.batch ? `${item.name} (${item.batch})` : item.name;
    },
    keyLabel(key) {
      return key.listName ? `${key.listName}: ${key.name}` : key.name;
    },
    operatorLabel(op) {
      return OPERATOR_LABELS[op] || op;
    },
    inputType(valueType) {
      switch (valueType) {
        case 'integer':
        case 'float':
          return 'number';
        case 'date':
          return 'date';
        case 'datetime':
          return 'datetime-local';
        default:
          return 'text';
      }
    },
    valueFields(row) {
      if (NO_VALUE_OPERATORS.includes(row.operator)) return [];
      return row.operator === 'between' ? ['value', 'value2'] : ['value'];
    },
    valueAria(row, field) {
      if (row.operator !== 'between') return 'Value';
      return field === 'value' ? 'Lower bound' : 'Upper bound';
    },
    rowError(row) {
      const result = validateMetadataRow(row);
      return result.status === 'invalid' ? result.error : '';
    },
    addRow() {
      this.update('metadata', [...this.modelValue.metadata, newMetadataRow()]);
    },
    removeRow(index) {
      this.update('metadata', this.modelValue.metadata.filter((_, i) => i !== index));
    },
    updateRow(index, patch) {
      this.update(
        'metadata',
        this.modelValue.metadata.map((row, i) => (i === index ? { ...row, ...patch } : row)),
      );
    },
    onKeyChange(index, key) {
      const operator = key.operators.includes('eq') ? 'eq' : key.operators[0];
      this.updateRow(index, { key, operator, value: '', value2: '' });
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/variables.scss";

.filter-panel {
  background: $form-color;
  border: 1px solid $secondary-color;
  border-radius: 8px;
  padding: 12px 16px 16px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;

  h4 {
    margin: 0;
    color: $primary-color;
  }
}

.badge {
  display: inline-block;
  min-width: 1.4em;
  margin-left: 6px;
  padding: 0 6px;
  border-radius: 999px;
  background: $primary-color;
  color: #fff;
  font-size: 0.75rem;
  text-align: center;
}

.filter-section {
  border-top: 1px solid $secondary-color;
  padding: 10px 0;

  summary {
    cursor: pointer;
    font-weight: 600;
    color: $primary-color;
    margin-bottom: 8px;

    &:focus-visible {
      outline: 2px solid $primary-color;
      outline-offset: 2px;
    }
  }
}

.field-label {
  display: block;
  margin: 8px 0 4px;
  font-size: 0.85rem;
}

input,
select {
  width: 100%;
  box-sizing: border-box;
  padding: 6px 8px;
  border: 1px solid #c8c8c0;
  border-radius: 5px;
  background: $form-color;

  &:focus-visible {
    outline: 2px solid $primary-color;
    outline-offset: 1px;
  }

  &[aria-invalid="true"] {
    border-color: #b3261e;
  }
}

.date-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.md-rows {
  list-style: none;
  margin: 0 0 8px;
  padding: 0;
}

.md-row {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px;
  margin-bottom: 8px;
  background: $global-bg-color;
  border: 1px solid $secondary-color;
  border-radius: 6px;
}

.value-inputs {
  display: flex;
  align-items: center;
  gap: 6px;
}

.unit,
.hint {
  font-size: 0.85rem;
  color: #555;
}

.field-error {
  margin: 0;
  font-size: 0.85rem;
  color: #b3261e;
}

.link-button {
  align-self: flex-start;
  padding: 2px 0;
  border: none;
  background: none;
  color: $primary-color;
  text-decoration: underline;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }

  &:focus-visible {
    outline: 2px solid $primary-color;
    outline-offset: 2px;
  }
}

.panel-footer {
  position: sticky;
  bottom: 0;
  padding-top: 10px;
  background: $form-color;
}

.apply-button {
  width: 100%;
  padding: 8px 16px;
  border: none;
  border-radius: 5px;
  background: $primary-color;
  color: #fff;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: darken($primary-color, 10%);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}
</style>
